import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import {
  Briefcase,
  HeadphonesIcon,
  Calculator,
  UserCircle,
  Building2,
  LogOut,
  Sparkles,
  Mail,
  Phone,
  Truck,
  Package,
} from 'lucide-react';

// Mirrors the role labels used in the AdminDashboard so any place we surface
// a role to the user reads the same way.
const ROLE_META = {
  assistant: {
    label: 'Assistant',
    description: 'Support the admin team with day-to-day operations.',
    Icon: Briefcase,
    accent: '#6366f1',
  },
  customer_support: {
    label: 'Customer Support Agent',
    description: 'First line of contact for customer inquiries.',
    Icon: HeadphonesIcon,
    accent: '#0ea5e9',
  },
  accountant: {
    label: 'Accountant',
    description: 'Tracks finances, orders, and reconciles the books.',
    Icon: Calculator,
    accent: '#16a34a',
  },
  courier: {
    label: 'Courier',
    description: 'Moves orders and stock between the central store and retail locations.',
    Icon: Truck,
    accent: '#ea580c',
  },
  inventory_manager: {
    label: 'Inventory & Operations Manager',
    description: 'Owns stock and coordinates day-to-day flow at the central warehouse.',
    Icon: Package,
    accent: '#0ea5e9',
  },
  operations_manager: {
    label: 'Inventory & Operations Manager',
    description: 'Owns stock and coordinates day-to-day flow at the central warehouse.',
    Icon: Package,
    accent: '#0ea5e9',
  },
  other: {
    label: 'Administration Staff',
    description: 'Part of the administration team.',
    Icon: UserCircle,
    accent: '#a855f7',
  },
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 0% 0%, rgba(99, 102, 241, 0.18), transparent 45%),
    radial-gradient(circle at 100% 0%, rgba(14, 165, 233, 0.15), transparent 45%),
    linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  padding: 3rem 1.5rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow:
    0 24px 60px -20px rgba(15, 23, 42, 0.25),
    0 8px 20px -8px rgba(15, 23, 42, 0.1);
  animation: ${fadeIn} 320ms ease-out;

  @media (max-width: 640px) {
    padding: 1.75rem;
    border-radius: 18px;
  }
`;

const Crown = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: ${(p) => `linear-gradient(135deg, ${p.$accent} 0%, ${p.$accent}dd 100%)`};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 12px 28px -8px ${(p) => `${p.$accent}66`};
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4338ca;
  background: #eef2ff;
  border-radius: 999px;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.875rem;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0 0 2rem;
  color: #475569;
  font-size: 1rem;
  line-height: 1.55;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.875rem;
  margin-bottom: 2rem;
`;

const InfoTile = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`;

const InfoLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`;

const InfoValue = styled.div`
  font-size: 0.9375rem;
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
`;

const Note = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 14px;
  padding: 1rem 1.125rem;
  color: #92400e;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.75rem;
`;

const LogoutButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.125rem;
  background: white;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;

  &:hover {
    background: #fef2f2;
  }
`;

/**
 * Landing page for administration-bound staff (assistant, customer_support,
 * accountant, other) until each role gets its dedicated dashboard. The page
 * confirms that login worked, shows the user where they sit in the org
 * (admin location, role) and lets them log out.
 */
const AdminStaffHome = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    // Hydrate from /auth/me so the data is fresh — the cached `user` in
    // localStorage may be stale right after an admin tweaks the user.
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }
        const response = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.user) {
          setProfile(response.data.user);
        } else {
          const cached = localStorage.getItem('user');
          if (cached) setProfile(JSON.parse(cached));
        }
      } catch (error) {
        // Fall back to whatever is cached so the screen still renders.
        const cached = localStorage.getItem('user');
        if (cached) setProfile(JSON.parse(cached));
      }
    };
    fetchMe();
  }, [navigate]);

  const meta = useMemo(() => {
    if (!profile?.role) return ROLE_META.other;
    return ROLE_META[profile.role] || ROLE_META.other;
  }, [profile]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('storeInfo');
    navigate('/');
  };

  const fullName = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
    : '';

  // Inventory managers, operations managers, and couriers all belong to
  // the central warehouse Store, not an AdministrationLocation.
  const isCentralStoreRole = ['inventory_manager', 'operations_manager', 'courier'].includes(profile?.role);
  const assignmentLabel = isCentralStoreRole ? 'Central Store' : 'Administration';
  const assignmentName = isCentralStoreRole
    ? (profile?.store?.name
        ? `${profile.store.name}${profile.store.code ? ` \u00b7 ${profile.store.code}` : ''}`
        : 'Not assigned yet')
    : (profile?.adminLocation?.name
        ? `${profile.adminLocation.name}${profile.adminLocation.adminCode ? ` \u00b7 ${profile.adminLocation.adminCode}` : ''}`
        : 'Not assigned yet');

  const Icon = meta.Icon;

  return (
    <Page>
      <Card>
        <Crown $accent={meta.accent}>
          <Icon size={32} />
        </Crown>
        <Eyebrow>
          <Sparkles size={12} />
          {meta.label}
        </Eyebrow>
        <Title>Welcome{fullName ? `, ${fullName}` : ''}</Title>
        <Subtitle>
          {meta.description} Your dedicated dashboard is being designed —
          for now you can sign in and out, and an admin will assign your tools soon.
        </Subtitle>

        <InfoGrid>
          <InfoTile>
            <InfoLabel>
              <UserCircle size={12} /> Employee ID
            </InfoLabel>
            <InfoValue>{profile?.employeeId || '—'}</InfoValue>
          </InfoTile>
          <InfoTile>
            <InfoLabel>
              <Briefcase size={12} /> Role
            </InfoLabel>
            <InfoValue>{meta.label}</InfoValue>
          </InfoTile>
          <InfoTile>
            <InfoLabel>
              <Building2 size={12} /> {assignmentLabel}
            </InfoLabel>
            <InfoValue>{assignmentName}</InfoValue>
          </InfoTile>
          {profile?.email && (
            <InfoTile>
              <InfoLabel>
                <Mail size={12} /> Email
              </InfoLabel>
              <InfoValue>{profile.email}</InfoValue>
            </InfoTile>
          )}
          {profile?.phone && (
            <InfoTile>
              <InfoLabel>
                <Phone size={12} /> Phone
              </InfoLabel>
              <InfoValue>{profile.phone}</InfoValue>
            </InfoTile>
          )}
        </InfoGrid>

        <Note>
          Need access to a specific tool sooner? Ask the system administrator
          to add the relevant permissions to your account.
        </Note>

        <Footer>
          <LogoutButton onClick={handleLogout}>
            <LogOut size={16} />
            Sign out
          </LogoutButton>
        </Footer>
      </Card>
    </Page>
  );
};

export default AdminStaffHome;
